import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ViewerHost } from "../ViewerHost";
import type { ViewerClient } from "../../client/ViewerClient";
import { useViewer } from "../../state/ViewerProvider";
import { Icon } from "../../ui/kit";
import { popVariants } from "../../ui/theme";
import { RingProvider } from "../../ui/ring/ringContext";
import { RingBuilder } from "../../ui/ring/RingBuilder";
import { parseRingSetDoc, replayCaps, restoreRingSet, type RingSetDoc } from "../../ui/ring/ringState";
import { capsFor } from "../../ui/editor/caps";
import { useToast } from "../../shared/ui/providers/ToastProvider";
import { loadDesignFromSearch } from "../../ui/ring/designStore";
import type { QualityTier } from "../../shared/api-contract";

/**
 * The merged Ring Builder route (`/ring`, and the unguarded `/dev/ring` for
 * headless harnesses). One builder: the wedding band is the base product and
 * the Engagement Ring toggle attaches the head — so this page replaces both
 * BandPage and EngagementPage (both still exist until the cleanup wave).
 *
 * `?design=<token>` restores a shared design; `?engagement=1` boots with the
 * engagement toggle on (that is what `/engagement` redirects to).
 */
export default function RingBuilderPage() {
  return (
    <ViewerHost mode="band">
      {({ ready, client, iframe }) => <RingBuilderBody ready={ready} client={client} iframe={iframe} />}
    </ViewerHost>
  );
}

function RingBuilderBody({
  ready,
  client,
  iframe,
}: {
  ready: boolean;
  client: ViewerClient | null;
  iframe: ReactNode;
}) {
  const params = new URLSearchParams(window.location.search);
  // True while a `?design=` token is being resolved and replayed on boot. Gates
  // the builder mount alongside `ready` so it never flashes the default design.
  const [designLoading, setDesignLoading] = useState(() => params.has("design"));
  /** The set that was actually restored — the builder seeds its rows, arrangement
   * and link state from it (v43: the document is the whole set, not one ring). */
  const [restored, setRestored] = useState<RingSetDoc | null>(null);
  const appliedRef = useRef(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!ready || !client || appliedRef.current) return;
    appliedRef.current = true;
    void (async () => {
      try {
        const json = await loadDesignFromSearch(window.location.search);
        if (json) {
          const v = client.contractVersion;
          const restoredSet = await restoreRingSet(client.ringBuilder, parseRingSetDoc(json), {
            multiRing: v >= 41,
            link: v >= 42,
            replay: replayCaps(capsFor(v)),
            // v68: a shared set's photographed pose and look (getBandLayout gates the pose).
            pose: capsFor(v).ringPoseV68,
            look: capsFor(v).ringLookV68,
          });
          setRestored(restoredSet);
          // What this viewer could not take back (e.g. path grooves on a pre-v66 viewer) is said, never dropped quietly.
          for (const why of restoredSet.left) toast({ tone: "warning", title: "Part of the shared design was left out", description: why });
        }
      } catch (err) {
        console.warn("[ring] could not restore ?design=", err);
      }
      setDesignLoading(false);
    })();
  }, [ready, client, toast]);

  const showLoading = !ready || designLoading;
  const stageRef = useRef<HTMLElement>(null);

  return (
    <div className="flex h-screen flex-col bg-background text-on-background">
      <header className="relative z-50 flex h-16 shrink-0 items-center justify-between gap-6 border-b border-outline-variant/30 bg-surface/90 px-8 backdrop-blur-md">
        <div className="flex items-center gap-5">
          <Link
            to="/vault"
            className="flex h-9 w-9 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-variant/50"
            title="Back to Vault"
          >
            <ArrowLeft size={18} />
          </Link>
          <span className="font-display text-[20px] font-light uppercase tracking-[0.28em] text-primary">
            Ring Builder
          </span>
        </div>
      </header>

      <main className="relative flex min-h-0 flex-1">
        {!showLoading ? (
          <RingProvider
            fallback={<div className="w-[440px] shrink-0 border-r border-outline-variant/30 bg-surface-container-low/40" />}
          >
            <RingBuilder
              defaultEngagement={params.get("engagement") === "1"}
              initialSet={restored ?? undefined}
            />
          </RingProvider>
        ) : null}

        <section ref={stageRef} className="relative flex flex-1 items-center justify-center bg-surface-bright">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(254,215,149,0.06)_0%,transparent_70%)]" />
          {iframe}
          {ready ? <ViewerTools stageRef={stageRef} /> : null}
          {/* Plain conditional + a CSS fade, deliberately NOT AnimatePresence.
              An exit animation that fails to settle leaves this overlay mounted
              forever — a permanent "LOADING" over a finished ring, which is
              exactly what happened here. Presence is the thing that must be
              correct; the fade is decoration, so it rides on CSS where it cannot
              hold the node hostage. */}
          {showLoading ? (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-surface-bright transition-opacity duration-300">
              <span className="animate-pulse font-display text-[11px] font-semibold uppercase tracking-[0.4em] text-on-surface-variant">
                Loading
              </span>
            </div>
          ) : null}
        </section>
      </main>
    </div>
  );
}

/**
 * The reference's floating tool cluster over the viewer.
 *
 * Its "Center" button is omitted: the viewer aliases `autoCenter()`/`autoFit()`
 * straight onto `resetView()`, so it would be a second button doing exactly what
 * "Reset view" does. Fullscreen is the native browser API on the stage element —
 * there is no fullscreen RPC and none is needed.
 */
function ViewerTools({ stageRef }: { stageRef: RefObject<HTMLElement | null> }) {
  const { client, catalogs } = useViewer();
  const [rotating, setRotating] = useState(false);
  const [full, setFull] = useState(false);
  // Start ON the preset the Ring Builder actually boots with, so the first
  // click steps OFF it rather than jumping to a random one — and so cycling all
  // the way round returns to the measured studio look. Before `bandStudio`
  // existed as a catalog entry, every preset was a URL-less procedural and one
  // click destroyed the look permanently (no id could restore the studio EXR).
  const envIndex = useRef(-1);
  if (envIndex.current === -1 && catalogs.envPresets.length) {
    const i = catalogs.envPresets.findIndex((e) => e.id === "bandStudio");
    envIndex.current = i >= 0 ? i : 0;
  }

  const cycleEnv = useCallback(() => {
    const envs = catalogs.envPresets;
    if (envs.length === 0) return;
    envIndex.current = (envIndex.current + 1) % envs.length;
    void client.ring.setEnvironment(envs[envIndex.current].id);
  }, [catalogs.envPresets, client]);

  const toggleRotate = useCallback(() => {
    setRotating((on) => {
      void client.ring.setAutoRotate(!on);
      return !on;
    });
  }, [client]);

  const toggleFullscreen = useCallback(() => {
    // Fullscreen the stage <section>, which carries the iframe and this cluster.
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    else void stageRef.current?.requestFullscreen().catch(() => undefined);
  }, [stageRef]);

  useEffect(() => {
    const onChange = () => setFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  return (
    <div className="absolute right-4 top-1/2 z-30 flex -translate-y-1/2 flex-col gap-1 rounded-full border border-outline-variant/40 bg-surface/70 p-1.5 backdrop-blur-md">
      <QualityMenu />
      <ToolButton icon="light_mode" title="Cycle environment" onClick={cycleEnv} />
      {/*
        v42 — `flipCamera` swings the camera half a turn about the orbit target
        to show the other face. It moves the CAMERA, not the ring: rotating the
        models would walk them out from under the set layout and invalidate the
        baked ground shadow.
      */}
      {client.contractVersion >= 42 ? (
        <ToolButton icon="flip_camera_android" title="Flip to the other side" onClick={() => void client.ring.flipCamera()} />
      ) : null}
      {/* The other half of the same cluster. `zoomCamera` is v26 — `finalParity`
          in caps terms — and needed no new RPC of its own. */}
      {client.contractVersion >= 26 ? (
        <ToolButton icon="zoom_out" title="Zoom out" onClick={() => void client.ring.zoomCamera(1.25)} />
      ) : null}
      <ToolButton icon="restart_alt" title="Reset view" onClick={() => void client.ring.resetView()} />
      <ToolButton icon="360" title="Auto-rotate" active={rotating} onClick={toggleRotate} />
      <ToolButton
        icon={full ? "fullscreen_exit" : "fullscreen"}
        title={full ? "Exit fullscreen" : "Fullscreen"}
        onClick={toggleFullscreen}
      />
    </div>
  );
}

const QUALITY_TIERS: { id: QualityTier; label: string }[] = [
  { id: "medium", label: "Medium" },
  { id: "high", label: "High" },
  { id: "ultra", label: "Ultra" },
];

/** Mirrors the viewer's own heuristic (`src/util/isMobile.ts`) — the two must agree
 *  or the chip names a tier the viewer did not boot at. */
const IS_MOBILE =
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
  (navigator.maxTouchPoints > 1 && window.innerWidth < 1024);

/**
 * Render-quality switch (contract v38). The tier is a pure client-side preference:
 * there is no getter to read back, so this state has to predict what the viewer
 * booted at — v43 `bandStudio` picks the tier by device, so a phone starts on
 * Medium and a desktop on High. A reload reboots the viewer at the same default.
 *
 * Hidden entirely on a pre-v38 viewer: `setQualityTier` would reject.
 */
function QualityMenu() {
  const { client } = useViewer();
  const [tier, setTier] = useState<QualityTier>(IS_MOBILE ? "medium" : "high");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (client.contractVersion < 38) return null;

  const pick = (id: QualityTier) => {
    setTier(id);
    setOpen(false);
    void client.ring.setQualityTier(id);
  };

  return (
    <div ref={ref} className="relative">
      <ToolButton icon="settings" title="Render quality" active={open} onClick={() => setOpen((o) => !o)} />
      <AnimatePresence>
        {open && (
          <motion.div
            variants={popVariants}
            initial="initial"
            animate="enter"
            exit="exit"
            className="absolute right-[calc(100%+10px)] top-1/2 flex -translate-y-1/2 gap-1 rounded-full border border-outline-variant/40 bg-surface-container-lowest p-1 shadow-[0_12px_40px_rgba(0,0,0,0.10)]"
          >
            {QUALITY_TIERS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => pick(t.id)}
                aria-pressed={tier === t.id}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 font-display text-[10px] font-semibold uppercase tracking-[0.1em] transition-colors ${
                  tier === t.id
                    ? "bg-secondary-container/30 text-secondary"
                    : "text-on-surface-variant hover:bg-surface-variant/50"
                }`}
              >
                {t.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ToolButton({
  icon,
  title,
  active,
  onClick,
}: {
  icon: string;
  title: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      onClick={onClick}
      className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
        active ? "bg-surface-container-high text-primary" : "text-on-surface-variant hover:bg-surface-variant/50"
      }`}
    >
      <Icon name={icon} size={18} />
    </button>
  );
}
